import React from "react";
import { FileText, CalendarClock, Settings } from "lucide-react";
import { DD_MM_YYYY, timeFormat } from "src/helpers";
import { EditOpsNote } from "src/components";
import { UpdateWarehouseCheckInOpsNoteParams, WarehouseCheckIn, WarehouseCheckInAdditionalInfo } from "src/types";
import { CollapsibleSection } from "src/components/ui/collapsible";

interface WarehouseCheckInOperationsSectionProps {
  warehouseCheckInId: string;
  timestamp?: string;
  operationsNote?: WarehouseCheckInAdditionalInfo["operationsNote"];
  onUpdateOpsNote?: (params: UpdateWarehouseCheckInOpsNoteParams) => void;
  initiallyOpen?: boolean;
  withCollapsible?: boolean;
}

const WarehouseCheckInOperationsSection: React.FC<WarehouseCheckInOperationsSectionProps> = ({
  warehouseCheckInId,
  timestamp,
  operationsNote,
  onUpdateOpsNote,
  initiallyOpen = true,
  withCollapsible = true
}) => {
  const formattedTimestamp =
    timestamp && `${DD_MM_YYYY.formatDate(new Date(timestamp))} at ${timeFormat(new Date(timestamp))}`;

  const content = (
    <div className="p-6 space-y-6">
      {formattedTimestamp && (
        <div className="bg-secondary-50 rounded-lg p-4">
          <div className="flex items-center space-x-2 mb-2">
            <CalendarClock className="w-4 h-4 text-primary-600" />
            <span className="text-xs font-semibold uppercase tracking-wide text-secondary-700">Received At</span>
          </div>
          <p className="text-sm font-medium text-secondary-900">{formattedTimestamp}</p>
        </div>
      )}

      <div className="bg-primary-50 border border-primary-200 rounded-lg p-4">
        <div className="flex items-start space-x-3">
          <FileText className="w-5 h-5 text-primary-600 mt-0.5 flex-shrink-0" />
          <div className="flex-1">
            <p className="text-sm font-semibold text-secondary-800 mb-2">Operations Note</p>
            <EditOpsNote<WarehouseCheckIn>
              id={warehouseCheckInId}
              timestamp={timestamp ?? ""}
              opsNote={operationsNote}
              onUpdateOpsNote={onUpdateOpsNote}
              isTextButton={true}
              textTruncateLength={1200}
            />
          </div>
        </div>
      </div>
    </div>
  );

  if (!withCollapsible) {
    return (
      <section className="rounded-2xl border border-secondary-200 bg-white shadow-soft">
        <div className="flex items-center gap-2 border-b border-secondary-100 px-6 py-4">
          <Settings className="h-5 w-5 text-primary-600" />
          <h3 className="text-base font-semibold text-secondary-900">Operations</h3>
        </div>
        {content}
      </section>
    );
  }

  return (
    <div className="bg-white border border-secondary-200 rounded-xl shadow-soft overflow-hidden">
      <CollapsibleSection
        initiallyOpen={initiallyOpen}
        title={
          <h3 className="text-lg font-semibold text-secondary-800 flex items-center">
            <Settings className="w-5 h-5 mr-2 text-primary-600" />
            Operations
          </h3>
        }
      >
        {content}
      </CollapsibleSection>
    </div>
  );
};

export { WarehouseCheckInOperationsSection };
